/**
 * Sélecteur multi d'ATTEMPTS (réponses enregistrées d'élèves) — pas d'exercices.
 * Le showcase rejoue un attempt : choisir des attempts garantit toujours un replay
 * valide. La vidéo enchaîne les attempts choisis DANS L'ORDRE (playlist), étirés
 * pour remplir la durée cible. Liste filtrable par titre d'exercice / nom d'élève.
 */

import { useMemo, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { Search, Check, ArrowUp, ArrowDown, X, GripVertical } from 'lucide-react';
import { SUBJECTS } from '@jeraisonne/db';
import { useVideoAttemptCandidates, type VideoAttemptCandidate } from '@/lib/admin/youtube-queries';
import { inputCls } from './ui';

export interface AttemptSelection {
  attemptId: string;
  exerciseId: string;
  title: string;
  niveau: string;
  chapitre: string;
  matiere: string;
}

interface AttemptPickerProps {
  /** Playlist ordonnée. `value[0]` = item primaire (métadonnées + miniature). */
  value: AttemptSelection[];
  onChange: (next: AttemptSelection[]) => void;
}

function toSelection(a: VideoAttemptCandidate): AttemptSelection {
  return {
    attemptId: a.attempt_id,
    exerciseId: a.exercise_id,
    title: a.exercise_title,
    niveau: a.level ? String(a.level) : '',
    chapitre: a.chapter_title ?? '',
    matiere: SUBJECTS.find((s) => s.value === a.subject)?.label ?? '',
  };
}

function move<T>(arr: T[], from: number, to: number): T[] {
  if (to < 0 || to >= arr.length) return arr;
  const next = [...arr];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

export function AttemptPicker({ value, onChange }: AttemptPickerProps) {
  const { data, isLoading, error } = useVideoAttemptCandidates();
  const [q, setQ] = useState('');

  const selectedIds = useMemo(() => new Set(value.map((v) => v.attemptId)), [value]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = data ?? [];
    if (!needle) return list;
    return list.filter(
      (a) =>
        a.exercise_title.toLowerCase().includes(needle) ||
        a.student_name.toLowerCase().includes(needle) ||
        (a.chapter_title ?? '').toLowerCase().includes(needle),
    );
  }, [data, q]);

  function toggle(a: VideoAttemptCandidate) {
    if (selectedIds.has(a.attempt_id)) {
      onChange(value.filter((v) => v.attemptId !== a.attempt_id));
    } else {
      onChange([...value, toSelection(a)]);
    }
  }

  return (
    <div className="space-y-4">
      {/* Playlist sélectionnée (ordonnée) */}
      {value.length > 0 && (
        <div className="space-y-2">
          <p className="font-display text-ink-500 text-[11px] font-bold uppercase tracking-widest">
            Playlist · {value.length} exercice{value.length > 1 ? 's' : ''}
          </p>
          <ul className="space-y-2">
            {value.map((sel, i) => (
              <li
                key={sel.attemptId}
                className="border-primary-200 bg-primary-50/60 flex items-center gap-2 rounded-xl border p-2"
              >
                <GripVertical className="text-ink-300 h-4 w-4 shrink-0" />
                <span className="bg-primary-500 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="font-display text-ink-900 block truncate text-sm font-bold">
                    {sel.title || 'Exercice'}
                  </span>
                  {(sel.chapitre || sel.niveau) && (
                    <span className="text-ink-500 block truncate text-xs">
                      {sel.chapitre}
                      {sel.niveau ? ` · ${sel.niveau}` : ''}
                      {i === 0 ? ' · primaire' : ''}
                    </span>
                  )}
                </span>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    aria-label="Monter"
                    disabled={i === 0}
                    onClick={() => onChange(move(value, i, i - 1))}
                    className="text-ink-500 hover:bg-ink-100 flex h-9 w-9 items-center justify-center rounded-lg active:scale-[.95] disabled:opacity-30"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Descendre"
                    disabled={i === value.length - 1}
                    onClick={() => onChange(move(value, i, i + 1))}
                    className="text-ink-500 hover:bg-ink-100 flex h-9 w-9 items-center justify-center rounded-lg active:scale-[.95] disabled:opacity-30"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Retirer"
                    onClick={() => onChange(value.filter((v) => v.attemptId !== sel.attemptId))}
                    className="text-accent-500 hover:bg-accent-50 flex h-9 w-9 items-center justify-center rounded-lg active:scale-[.95]"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <p className="text-ink-400 font-sans text-xs">
            Le 1<sup>er</sup> exercice fournit le titre, la matière et la miniature.
          </p>
        </div>
      )}

      {/* Recherche + liste des candidats */}
      <div className="relative">
        <Search className="text-ink-400 pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        <input
          className={cn(inputCls, 'pl-9')}
          placeholder="Filtrer par exercice, élève ou chapitre…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {error ? (
        <p className="text-accent-600 font-sans text-xs">
          Erreur de chargement des attempts : {(error as Error).message}
        </p>
      ) : isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-ink-100 h-14 animate-pulse rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-ink-400 font-sans text-xs">
          Aucun attempt « completed » trouvé. Un exercice doit avoir été terminé par un élève pour
          être filmé.
        </p>
      ) : (
        <div className="border-ink-200 divide-ink-100 max-h-80 divide-y overflow-y-auto rounded-xl border">
          {filtered.map((a) => {
            const selected = selectedIds.has(a.attempt_id);
            return (
              <button
                key={a.attempt_id}
                type="button"
                onClick={() => toggle(a)}
                className={cn(
                  'flex min-h-11 w-full items-center gap-3 p-3 text-left transition-colors active:scale-[.99]',
                  selected ? 'bg-primary-50' : 'hover:bg-ink-50 bg-white',
                )}
              >
                <span
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                    selected ? 'border-primary-500 bg-primary-500 text-white' : 'border-ink-300',
                  )}
                >
                  {selected && <Check className="h-3 w-3" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="font-display text-ink-900 block truncate text-sm font-bold">
                    {a.exercise_title}
                  </span>
                  <span className="text-ink-500 block truncate text-xs">
                    {a.student_name}
                    {a.level ? ` · ${a.level}` : ''}
                    {a.completed_at
                      ? ` · ${new Date(a.completed_at).toLocaleDateString('fr-FR')}`
                      : ''}
                    {` · ${a.content_version.toUpperCase()}`}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
